import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import useLazyCursorPagination from "../../hooks/useLazyCursorPagination";
import AccountsService from "../../services/AccountsService";
import { cn } from "../../utils/styles.utils";

const ACCOUNT_COLUMNS = [
  { key: "serialNumber", label: "S.NO", minWidth: 100, sortable: false },
  { key: "company", label: "Company", minWidth: 240, sortable: true },
  { key: "domain", label: "Domain", minWidth: 280, sortable: true },
  { key: "activeFrom", label: "Active From", minWidth: 180, sortable: true },
  { key: "activeTo", label: "Active To", minWidth: 180, sortable: true },
  { key: "revenue", label: "Revenue", minWidth: 160, sortable: true },
];

const DEFAULT_SORT_KEY =
  ACCOUNT_COLUMNS.find((column) => column.sortable)?.key ?? "company";
const MAX_VISIBLE_ROWS = 6;
const COLUMN_SCROLL_THRESHOLD = 3;
const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 56;
const CURSOR_BATCH_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const FALLBACK_ACCOUNTS = [
  {
    rowKey: "demo-account-1",
    company: "Demo Company",
    domain: "demo.prospectpulse.com",
    activeFrom: "03 Mar 2026",
    activeTo: "03 Mar 2027",
    revenue: "Demo Revenue",
  },
];

const compareAccounts = (accountA, accountB, sortConfig) => {
  const valueA = accountA?.[sortConfig.key];
  const valueB = accountB?.[sortConfig.key];

  let compareResult = 0;
  if (typeof valueA === "number" && typeof valueB === "number") {
    compareResult = valueA - valueB;
  } else {
    compareResult = String(valueA ?? "").localeCompare(
      String(valueB ?? ""),
      undefined,
      {
        numeric: true,
        sensitivity: "base",
      },
    );
  }

  return sortConfig.direction === "asc" ? compareResult : -compareResult;
};

const AccountsBoard = () => {
  const [sortConfig, setSortConfig] = useState({
    key: DEFAULT_SORT_KEY,
    direction: "asc",
  });
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchValue]);

  const fetchAccountsPage = useCallback(
    async ({ limit, cursor }) => {
      const response = await AccountsService.getAccountsCursor({
        limit,
        cursor,
        search: debouncedSearchValue,
      });

      if (!cursor && !debouncedSearchValue && response.data.length === 0) {
        return {
          data: FALLBACK_ACCOUNTS,
          nextCursor: null,
        };
      }

      return response;
    },
    [debouncedSearchValue],
  );

  const {
    rows: visibleAccounts,
    hasMore,
    isLoading,
    hasLoadedOnce,
    containerRef,
  } = useLazyCursorPagination(fetchAccountsPage, CURSOR_BATCH_SIZE);

  const accountsToDisplay =
    hasLoadedOnce &&
    !isLoading &&
    !debouncedSearchValue &&
    visibleAccounts.length === 0
      ? FALLBACK_ACCOUNTS
      : visibleAccounts;

  const sortedAccounts = useMemo(() => {
    const rows = [...accountsToDisplay];
    rows.sort((accountA, accountB) =>
      compareAccounts(accountA, accountB, sortConfig),
    );
    return rows;
  }, [accountsToDisplay, sortConfig]);

  const handleHeaderClick = useCallback((columnKey) => {
    setSortConfig((previousSort) => {
      if (previousSort.key === columnKey) {
        return {
          key: columnKey,
          direction: previousSort.direction === "asc" ? "desc" : "asc",
        };
      }

      return { key: columnKey, direction: "asc" };
    });
  }, []);

  const headerClickHandlers = useMemo(
    () =>
      ACCOUNT_COLUMNS.reduce((handlers, column) => {
        if (column.sortable) {
          handlers[column.key] = () => handleHeaderClick(column.key);
        }
        return handlers;
      }, {}),
    [handleHeaderClick],
  );

  const shouldEnableHorizontalScroll =
    ACCOUNT_COLUMNS.length > COLUMN_SCROLL_THRESHOLD;
  const tableViewportHeight =
    TABLE_HEADER_HEIGHT + MAX_VISIBLE_ROWS * TABLE_ROW_HEIGHT;
  const shouldShowLoadingState = !hasLoadedOnce && isLoading;
  const shouldShowEmptyState =
    hasLoadedOnce && !isLoading && sortedAccounts.length === 0;
  const shouldShowLoadMoreHint =
    hasLoadedOnce && !isLoading && sortedAccounts.length > 0 && hasMore;

  return (
    <div className="mb-4 min-w-0">
      <h6 className="fw-bold mb-3">Accounts</h6>
      <div className="mb-3">
        <input
          type="text"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          placeholder="Search table..."
          aria-label="Search table"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors duration-150 placeholder:text-slate-400 focus:border-sky-400"
        />
      </div>
      <div
        ref={containerRef}
        className="w-full min-w-0 max-w-full rounded-2xl"
        style={{
          overflowX: shouldEnableHorizontalScroll ? "auto" : "hidden",
          overflowY: "auto",
          maxHeight: `${tableViewportHeight}px`,
          maxWidth: "100%",
          overscrollBehavior: "contain",
        }}
      >
        <Table
          className="w-max min-w-full"
          searchable={false}
          searchMode="server"
          stickyHeader
          stickyHeaderOffset={0}
        >
          <TableHeader>
            <TableRow>
              {ACCOUNT_COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  align={column.align ?? "left"}
                  onClick={headerClickHandlers[column.key]}
                  sortable={column.sortable}
                  className={cn(
                    "whitespace-nowrap sticky -top-[2px] z-10 bg-slate-100",
                    column.sortable && "cursor-pointer user-select-none",
                  )}
                  style={{ minWidth: `${column.minWidth}px` }}
                  aria-sort={
                    column.sortable && sortConfig.key === column.key
                      ? sortConfig.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shouldShowLoadingState ? (
              <TableRow>
                <TableCell
                  colSpan={ACCOUNT_COLUMNS.length}
                  className="text-center text-slate-500"
                >
                  Loading accounts...
                </TableCell>
              </TableRow>
            ) : null}
            {shouldShowEmptyState ? (
              <TableRow>
                <TableCell
                  colSpan={ACCOUNT_COLUMNS.length}
                  className="text-center text-slate-500"
                >
                  No accounts available.
                </TableCell>
              </TableRow>
            ) : null}
            {sortedAccounts.map((account, rowIndex) => {
              const rowKey = account.rowKey ?? `row-${rowIndex + 1}`;

              return (
                <TableRow key={rowKey}>
                  {ACCOUNT_COLUMNS.map((column) => (
                    <TableCell
                      key={`${column.key}-${rowKey}`}
                      className="whitespace-nowrap break-normal"
                      style={{ minWidth: `${column.minWidth}px` }}
                    >
                      {column.key === "serialNumber"
                        ? rowIndex + 1
                        : (account[column.key] ?? "-")}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {shouldShowLoadMoreHint ? (
              <TableRow>
                <TableCell
                  colSpan={ACCOUNT_COLUMNS.length}
                  className="text-center text-slate-400"
                >
                  Scroll to load more accounts...
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AccountsBoard;
